import { ChangeDetectorRef, Component, DestroyRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { SecurityTransactionFrom, SecurityTransactionTo } from '@models/security-transactions/entity-security-transfer';
import { SecurityTransactionType } from '@models/security-transactions/security-transaction-type.enum';
import { SecurityHolding } from '@models/shares/security-holding';
import { SecurityRegistryRecord } from '@models/shares/security-registry-record';
import { DividerComponent } from '@shared/components/commons/divider/divider.component';
import { InputNumberComponent } from '@shared/controls/input-number/input-number.component';
import { ValidationErrorComponent } from '@shared/controls/validation-error/validation-error.component';
import { Guid } from '@shared/utils/guid.helper';
import { NumbersValidators } from '@shared/validators/numbers.validators';
import {
  ShareSecurityHoldingsDropdownComponent
} from '../../../controls/share-security-holdings-dropdown/share-security-holdings-dropdown.component';
import {
  ShareholderWithNboSignComponent,
  ShareholderWithSignControls
} from '../../../controls/shareholder-with-nbo-sign/shareholder-with-nbo-sign.component';
import { CompanySecurityTransfer } from "../../../models/shares";
import {
  customErrorsOfShareCancellationNumbers,
  totalNumberOfSharesCustomErrors
} from '../../../models/shares/company-security-cancellation.model';
import {
  ReusableFormGroupComponent
} from '../../../reusable-form-groups/reusable-form-group/reusable-form-group.component';
import {
  CompanySecurityTransferToBlock
} from '../company-security-transfer-to-block/share-transfer-to-block.component';

export interface IShareTransferFromFormGroupControls {
  securityHoldingId: FormControl<string | null>;
  considerationPaid: FormControl<number | null>;
  numberIncrease: FormControl<number | null>;
  paidIncrease: FormControl<number | null>;
  unpaidIncrease: FormControl<number | null>;

  shareholder: FormGroup<ShareholderWithSignControls>;
}

export type IShareTransferFromFormGroup = FormGroup<IShareTransferFromFormGroupControls>;

@Component({
  selector: 'app-share-transfer-from-block',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ShareholderWithNboSignComponent,
    DividerComponent,
    ShareSecurityHoldingsDropdownComponent,
    InputNumberComponent,
    ValidationErrorComponent
  ],
  templateUrl: './share-transfer-from-block.component.html',
})
export class CompanySecurityTransferFromBlock extends ReusableFormGroupComponent<IShareTransferFromFormGroup> implements OnInit {
  @ViewChild(ShareholderWithNboSignComponent) shareholderWithNboFrom!: ShareholderWithNboSignComponent;

  @Input({ required: true }) securityRegistryRecords: SecurityRegistryRecord[] = [];
  @Input({ required: true }) transferToBlock?: CompanySecurityTransferToBlock;

  cdr = inject(ChangeDetectorRef);

  destroyRef = inject(DestroyRef);

  securityRegistryRecordsHoldings: SecurityHolding[] = [];

  selectedShareholderShareType: SecurityHolding | null = null;

  readonly totalNumberOfSharesCustomErrors = totalNumberOfSharesCustomErrors;
  readonly customErrorsOfShareCancellationNumbers = customErrorsOfShareCancellationNumbers;

  public override ngOnInit(): void {
    super.ngOnInit();
    this.setupFormChangeListeners();
    this.setCustomFormValidators();
  }

  setupFormChangeListeners(): void {
    this.listenSecurityRegistryRecordGroupChanges();
    this.listenSecurityHoldingIdChanges();
    this.listenNumberOfSharesChanges();
    this.listenSelectExistingShareholderChanges();
  }

  private listenSelectExistingShareholderChanges(): void {
    this.transferToBlock?.form?.controls?.selectShareholder?.controls?.securityRegistryRecordGroup?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((record) => {
        if (record?.length) {
          const isNotBeneficialOwner = !(record[0].isBeneficialOwner ?? record[0].holders[0].details.isBeneficialOwner);
          const beneficialOwner = (record[0].beneficialOwner ?? record[0].holders[0].details.beneficialOwner ?? null);

          this.transferToBlock?.form.controls.selectShareholder.controls.isNotBeneficialOwner.setValue(isNotBeneficialOwner);
          this.transferToBlock?.form.controls.selectShareholder.controls.beneficialOwner.setValue(beneficialOwner);
        }
      });
  }

  private listenSecurityRegistryRecordGroupChanges(): void {
    this.form.controls.shareholder.controls.securityRegistryRecordGroup.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((group) => {
        this.transferToBlock?.form?.controls?.selectShareholder?.controls?.securityRegistryRecordGroup?.setValue(null);

        if (group === null) {
          this.securityRegistryRecordsHoldings = [];
          this.form.controls.securityHoldingId.setValue(null);
          return;
        }
      });
  }

  private listenSecurityHoldingIdChanges(): void {
    this.form.controls.securityHoldingId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((securityHoldingId) => {
        this.transferToBlock?.form?.controls?.securityHoldingId?.setValue(securityHoldingId);
        this.selectedShareholderShareType = this.securityRegistryRecordsHoldings
          ?.find(holding => holding.securityHoldingId === securityHoldingId) ?? null;
        if (this.selectedShareholderShareType) {
          // cancelled shares >=0 && <= number of shares
          this.setNumberControlValidators(this.form.controls.numberIncrease, () => (this.selectedShareholderShareType?.number ?? 0));
          this.form.controls.numberIncrease.addValidators([NumbersValidators.isInteger]);
          // total paid >=0 && <= prefilled total paid
          this.setNumberControlValidators(this.form.controls.paidIncrease, () => (this.selectedShareholderShareType?.paid ?? 0));
          // total unpaid >=0 && <= prefilled total unpaid
          this.setNumberControlValidators(this.form.controls.unpaidIncrease, () => (this.selectedShareholderShareType?.unpaid ?? 0));
        }
        this.form.controls.numberIncrease.setValue(this.selectedShareholderShareType?.number ?? 0);
      });
  }

  private listenNumberOfSharesChanges(): void {
    this.form.controls.numberIncrease.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((numberOfShares) => {
        this.transferToBlock?.form?.controls?.numberOfSharesTransferred?.setValue(numberOfShares);
        if (numberOfShares === null || this.selectedShareholderShareType === null) {
          return;
        }

        this.form.controls.considerationPaid.setValue(numberOfShares * this.selectedShareholderShareType.securityType.paidAmount);
        this.form.controls.paidIncrease.setValue(numberOfShares * this.selectedShareholderShareType.securityType.paidAmount);
        this.form.controls.unpaidIncrease.setValue(numberOfShares * this.selectedShareholderShareType.securityType.unpaidAmount);
      });
  }

  private setCustomFormValidators(): void {
    const paidTotalValidator: ValidatorFn = () => {
      if (this.selectedShareholderShareType === null || !this.form.value.paidIncrease || !this.form.value.numberIncrease) {
        return null;
      }
      // (Math.abs(prefilled total paid - total paid))/prefilled total paid < 0.05

      const calculatedTotalPaid = this.selectedShareholderShareType.securityType.paidAmount * this.form.value.numberIncrease;
      return (Math.abs(calculatedTotalPaid - this.form.value.paidIncrease) / calculatedTotalPaid) < 0.05
        ? null
        : { paidChangeIsBiggerThan5Percent: true };
    };

    const unpaidTotalValidator: ValidatorFn = () => {
      if (this.selectedShareholderShareType === null || !this.form.value.unpaidIncrease || !this.form.value.numberIncrease) {
        return null;
      }
      // (Math.abs(prefilled total unpaid - total unpaid))/prefilled total unpaid < 0.05

      const calculatedTotalPaid = this.selectedShareholderShareType.securityType.unpaidAmount * this.form.value.numberIncrease;
      return (Math.abs(calculatedTotalPaid - this.form.value.unpaidIncrease) / calculatedTotalPaid) < 0.05
        ? null
        : { unpaidChangeIsBiggerThan5Percent: true };
    };

    // total paid + total unpaid > 0
    const paidUnpaidSumValidator: ValidatorFn = () => {
      const totalPaid = this.form.controls.paidIncrease.getRawValue();
      const totalUnpaid = this.form.controls.unpaidIncrease.getRawValue();
      if (totalPaid === null || totalUnpaid === null) {
        return null;
      }

      return (totalPaid + totalUnpaid) > 0
        ? null
        : { paidUnpaidSumIsNotPositive: true };
    };

    this.form.setValidators([
      paidTotalValidator,
      unpaidTotalValidator,
      paidUnpaidSumValidator
    ]);
  }

  public static override defineFormControls(): IShareTransferFromFormGroupControls {
    return {
      securityHoldingId: new FormControl<string | null>(null, [Validators.required]),
      considerationPaid: new FormControl<number | null>(null, [Validators.required]),
      numberIncrease: new FormControl<number | null>(null, [Validators.required, NumbersValidators.min(0), NumbersValidators.isInteger]),
      paidIncrease: new FormControl<number | null>(null, [Validators.required]),
      unpaidIncrease: new FormControl<number | null>(null, [Validators.required]),

      shareholder: ShareholderWithNboSignComponent.defineForm()
    };
  }

  get formShareNumberErrorMessageHidden(): boolean {
    return Boolean(this.form.controls.numberIncrease?.untouched
      && this.form.controls.paidIncrease?.untouched
      && this.form.controls.unpaidIncrease?.untouched
      && !this.form?.errors);
  }

  private setNumberControlValidators(control: FormControl, maxFn: () => number, min = 0): void {
    control.setValidators([Validators.required, NumbersValidators.min(min), NumbersValidators.relativeMax(maxFn.bind(this))]);
    control.updateValueAndValidity();
  }

  public setupChange(change: CompanySecurityTransfer) {
    this.shareholderWithNboFrom.setupChange(change);
    this.onSelectCurrentShareholder(this.form.controls.shareholder.controls.securityRegistryRecordGroup.value ?? [])

    const securityHoldingId = change?.transferFrom?.securityHoldingId;

    if (securityHoldingId) {
      this.form.controls.securityHoldingId.setValue(securityHoldingId);
    }

    this.cdr.detectChanges();

    this.form.patchValue({
      ...change,
      ...change.transferFrom,

      considerationPaid: change.transferFrom.considerationPaid,
      numberIncrease: -change.transferFrom.numberIncrease,
      paidIncrease: -change.transferFrom.paidIncrease,
      unpaidIncrease: -change.transferFrom.unpaidIncrease,
    });
  }

  private get selectedSecurityRecordFrom(): SecurityRegistryRecord | null {
    return this.securityRegistryRecords
      ?.find(record => record.holdings.some(holder => holder.securityHoldingId === this.form.value.securityHoldingId)) ?? null;
  }

  buildChange(): CompanySecurityTransfer {
    const companySecurityTransfer = new CompanySecurityTransfer({
      $type: 'CompanySecurityTransfer',
      description: 'Company share transfer',
      transferFrom: new SecurityTransactionFrom({
        organisationId: Guid.EmptyGuid,
        transactionType: SecurityTransactionType.TransferFrom,
        securityHoldingId: this.form.controls.securityHoldingId.value ?? null,
        considerationPaid: this.form.controls.considerationPaid.value!,
        numberIncrease: -this.form.controls.numberIncrease.value!,
        paidIncrease: -this.form.controls.paidIncrease.value!,
        unpaidIncrease: -this.form.controls.unpaidIncrease.value!,
        securityTypeId: this.selectedShareholderShareType!.securityType.securityTypeId,
        securityType: this.selectedShareholderShareType!.securityType,
        relationshipIds: this.selectedSecurityRecordFrom!.holders.map(h => h.relationshipId)
      }),
      transferTo: new SecurityTransactionTo({
        organisationId: Guid.EmptyGuid,
        transactionType: SecurityTransactionType.TransferTo,
        securityHoldingId: Guid.EmptyGuid,
        considerationPaid: this.form.controls.considerationPaid.value!,
        numberIncrease: this.form.controls.numberIncrease.value!,
        paidIncrease: this.form.controls.paidIncrease.value!,
        unpaidIncrease: this.form.controls.unpaidIncrease.value!,
        securityTypeId: this.selectedShareholderShareType!.securityType.securityTypeId,
        securityType: this.selectedShareholderShareType!.securityType
      }),
      securityHoldersTo: this.transferToBlock?.getSecurityHoldersTo()
    });

    companySecurityTransfer.transferTo.relationshipIds = companySecurityTransfer.securityHoldersTo.map(h => h.relationshipId).filter(Boolean);
    return companySecurityTransfer;
  }

  onSelectCurrentShareholder(recordGroup: SecurityRegistryRecord[], shouldPrefillSecurityType = true): void {
    if (!this.transferToBlock) return;

    this.transferToBlock.transferSecurityRegistryRecords = this.securityRegistryRecords
      .filter((record) =>
        recordGroup !== null
        && !recordGroup?.includes(record));

    this.securityRegistryRecordsHoldings = recordGroup?.flatMap((record) => record.holdings) ?? null;
    this.transferToBlock.securityRegistryRecordsHoldings = this.securityRegistryRecordsHoldings;
    this.selectedShareholderShareType = this.securityRegistryRecordsHoldings?.[0] ?? null;
    this.transferToBlock.selectedShareholderShareType = this.selectedShareholderShareType;
    this.cdr.detectChanges();

    if (shouldPrefillSecurityType) {
      const securityHoldingId = this.selectedShareholderShareType?.securityHoldingId ?? null;
      this.form.controls.securityHoldingId.setValue(securityHoldingId);
      this.cdr.detectChanges();
    }
  }
}
